import React from 'react'
import styled from 'styled-components'

import { CurrencyInformationSocial } from '@root/entities/currency/ui'
import { DomUtil } from '@root/shared/core'
import { RectangleNetworkBackgroundGradient } from '@root/shared/ui'

import { CurrencyBannerFallback } from './currency-banner-fallback'

import type { ImageHash } from '@root/entities/currency'
import type { PairModel } from '@root/entities/pair'
import type { PremiumOwner } from '@root/entities/premium'
import type { Socials } from '@root/entities/socials'

type Props = {
  premium: boolean
  socials: Socials.Socials
  banner: string | undefined
  owner: PremiumOwner | undefined
  pair: PairModel
  footerSlot: React.ReactNode
  archiveButtonSlot: React.ReactNode
  iconSrcOverride?: string | undefined
  bannerSrcOverride?: string | undefined
  imageHashOverride?: ImageHash | undefined
}

export const CurrencyBanner: React.FC<Props> = (props) => {
  const {
    premium,
    socials,
    banner,
    bannerSrcOverride,
    iconSrcOverride,
    pair,
    footerSlot,
    archiveButtonSlot,
    imageHashOverride,
  } = props

  const renderContent = () => {
    return (
      <Content>
        <CurrencyInformationSocial socials={socials} variant="detailed" />
        {archiveButtonSlot ? (
          <PremiumArchivedButtonSlot>
            {archiveButtonSlot}
          </PremiumArchivedButtonSlot>
        ) : undefined}
      </Content>
    )
  }

  return (
    <Root data-premium={premium}>
      <Banner>
        {banner ? (
          <BannerImage
            style={{
              backgroundImage: [
                DomUtil.cssUrl(banner),
                RectangleNetworkBackgroundGradient,
              ].join(','),
            }}
          >
            {renderContent()}
          </BannerImage>
        ) : (
          <CurrencyBannerFallback
            pair={pair}
            iconSrcOverride={iconSrcOverride}
            bannerSrcOverride={bannerSrcOverride ?? 'none'}
            imageHashOverride={imageHashOverride}
          >
            {renderContent()}
          </CurrencyBannerFallback>
        )}
      </Banner>
      {footerSlot}
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  padding: 1px;
  border-radius: 12px;
  width: min-content;

  &[data-premium='true'] {
    border: 1px solid var(--subscriptionGradientFrom-9);
  }
  &[data-premium='false'] {
    border: 1px solid var(--background-2);
  }
`

const Banner = styled.div`
  width: 360px;
  height: 120px;
  overflow: hidden;
  border-radius: 10px;

  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  position: relative;
`

const BannerImage = styled.div`
  height: 100%;
  width: 100%;

  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  background-color: var(--background-1);
  position: relative;
`

const Content = styled.div`
  padding: 5px;
  position: absolute;
  inset: 0;

  display: flex;
  align-items: end;
  justify-items: start;
  width: 100%;
`
const PremiumArchivedButtonSlot = styled.div`
  margin-left: auto;
`
